import { useMemo } from "react";
import { DollarSignIcon, PercentIcon } from "lucide-react";
import AnalyticsSummaryBar, {
  CreatorAnalytics,
} from "@/components/dashboard_campaign/analytics/AnalyticsSummaryBar";
import AnalyticsTable from "@/components/dashboard_campaign/analytics/AnalyticsTable";
import VideoAnalyticsTable from "@/components/dashboard_campaign/analytics/VideoAnalyticsTable";
import FunnelChart, { FunnelStep } from "@/components/ui/funnel-chart";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAnalyticsStore } from "@/stores/creators/analyticsStore";
import { Creator } from "@/types/types";

/**
 * Read-only analytics view for a public share link. Reuses the dashboard's
 * leaf components (summary bar / funnel / tables); data comes via props and the
 * stores hydrated by SharePage. No hashtag editor, no add/delete affordances.
 */
export default function ShareAnalyticsBody({
  results,
  creators,
  useDub,
}: {
  results: CreatorAnalytics[];
  creators: Creator[];
  useDub: boolean;
}) {
  const { selectedTab, setSelectedTab } = useAnalyticsStore();

  // Post-activation creators are mapped to a synthetic "activated" status in SharePage.
  const postActivationCreators = useMemo(
    () => creators.filter((c) => c.status === "activated"),
    [creators]
  );

  const conversionFunnelData: FunnelStep[] = useMemo(() => {
    if (!results?.length) return [];
    const totalClicks = results.reduce((s, c) => s + c.clicks, 0);
    const totalLeads = results.reduce((s, c) => s + c.leads, 0);
    const totalSales = results.reduce((s, c) => s + c.sales, 0);
    const leadsPercent = totalClicks > 0 ? (totalLeads / totalClicks) * 100 : 0;
    const salesPercent = totalClicks > 0 ? (totalSales / totalClicks) * 100 : 0;
    return [
      { label: "Clicks", value: 100, color: "dark_orange" },
      { label: "Leads", value: leadsPercent, color: "orange" },
      { label: "Sales", value: salesPercent, color: "light_orange" },
    ];
  }, [results]);

  return (
    <div className="flex flex-col h-full w-full max-w-7xl mx-auto p-5 gap-2 overflow-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]">
      <div className="flex flex-col border rounded-lg shadow-xs relative overflow-hidden shrink-0">
        <AnalyticsSummaryBar data={results} />
        {useDub && (
          <Tabs
            value={selectedTab}
            onValueChange={(value) =>
              setSelectedTab(value as "conversion" | "video")
            }
            className="absolute top-3 right-3"
          >
            <TabsList className="border bg-background shadow-xs gap-0.5">
              <TabsTrigger
                value="conversion"
                className="hover:bg-sidebar text-xs text-muted-foreground data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 h-7 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none hover:text-foreground"
              >
                <DollarSignIcon className="size-3.5" strokeWidth={2.5} />
              </TabsTrigger>
              <TabsTrigger
                value="video"
                className="hover:bg-sidebar text-xs text-muted-foreground data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 h-7 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none hover:text-foreground"
              >
                <PercentIcon className="size-3.5" strokeWidth={2.5} />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        {selectedTab === "conversion" && (
          <FunnelChart steps={conversionFunnelData} height={85} />
        )}
      </div>

      <div className="flex-1 flex flex-col gap-2">
        {selectedTab === "conversion" ? (
          <AnalyticsTable data={results} />
        ) : (
          <VideoAnalyticsTable creators={postActivationCreators} readOnly />
        )}
      </div>
    </div>
  );
}
